import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import uuidV4 from 'uuid/v4';
import Modal from '@spothero/ui/modal';
import ModalContent from '@spothero/ui/modal-content';
import Tab from '@spothero/ui/tab';
import TabContent from '@spothero/ui/tab-content';
import TabNavigation from '@spothero/ui/tab-navigation';
import TabPanel from '@spothero/ui/tab-panel';
import Tabs from '@spothero/ui/tabs';
import SpotUtils from 'utils/spot';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import WithConfig from 'config/WithConfig';
import {push} from 'router/router-actions';
import {setTerm} from 'search-request/search-request-actions';
import {
    setSpotSelectedRate,
    cancelSpotRequests
} from 'spots/spots-actions';
import {notify} from 'notification/notification-actions';
import Icon from 'common/icon/Icon';
import WhyTimeExtendedModalContent from 'common/extended-time/WhyTimeExtendedModalContent';
import RateDetailsModalContent from './modals/RateDetailsModalContent';
import OnlineCommuterRateModalContent from './modals/OnlineCommuterRateModalContent';
import OversizedFeeDetailsModalContent from './modals/OversizedFeeDetailsModalContent';
import ReservationDetailsDesktop from './reservation-details/ReservationDetailsDesktop';
import SpotInfoTabDesktop from './details/SpotInfoTabDesktop';
import AccessHours from './access/AccessHours';
import GettingThere from './getting-there/GettingThere';
import HowToRedeem from './redeem/HowToRedeem';
import HeroControlsTabDesktop from './hero-controls/HeroControlsTabDesktop';
import LargerImagesModalContentDesktop from './details/LargerImagesModalContentDesktop';

class SpotDetailsModalDesktop extends Component {
    static propTypes = {
        spot: PropTypes.object,
        user: PropTypes.object,
        searchParams: PropTypes.object,
        isMonthly: PropTypes.bool.isRequired,
        isRebookingReservation: PropTypes.bool,
        timezone: PropTypes.string.isRequired,
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string,
        onHidden: PropTypes.func.isRequired,
        setSearchTerm: PropTypes.func.isRequired,
        setNewSpotRate: PropTypes.func.isRequired,
        cancelAllSpotRequests: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        event: PropTypes.object,
        sendNotification: PropTypes.func.isRequired
    };
    state = {
        prevSpot: this.props.spot,
        activeContentIndex: 0,
        updatedSpotData: null,
        rateDetailsSection: null
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            spot
        } = nextProps;
        const {
            prevSpot
        } = prevState;
        const newState = {};

        if (spot.spotId !== prevSpot.spotId) {
            newState.prevSpot = spot;
            newState.previousContentIndex = null;
            newState.activeContentIndex = 0;
            newState.updatedSpotData = null;
            newState.rateDetailsSection = null;
        }

        if (!prevSpot.hasTravelDistanceUpdated && spot.hasTravelDistanceUpdated) {
            newState.updatedSpotData = nextProps.spot;
        }

        return (!isEmpty(newState)) ? newState : null;
    }

    componentDidMount() {
        this._initSpotDetails();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.spot.spotId !== this.props.spot.spotId) {
            this._initSpotDetails();
        }
    }

    componentWillUnmount() {
        if (this._rateLoader) {
            this._rateLoader.cancel();
            this._rateLoader = null;
        }
    }

    _onTransientAvailableClick = () => {
        GTMUtils.push({
            event: 'GAEvent',
            eventCategory: 'link',
            eventAction: 'click',
            eventLabel: 'hourly_rate_toggle_from_spot_details_modal',
            eventValue: undefined, // eslint-disable-line no-undefined
            hitCallback: undefined // eslint-disable-line no-undefined
        });

        this.props.setSearchTerm({term: 'daily'});
    }

    _onMonthlyAvailableClick = () => {
        GTMUtils.push({
            event: 'GAEvent',
            eventCategory: 'link',
            eventAction: 'click',
            eventLabel: 'monthly_rate_toggle_from_spot_details_modal',
            eventValue: undefined, // eslint-disable-line no-undefined
            hitCallback: undefined // eslint-disable-line no-undefined
        });

        this.props.setSearchTerm({term: 'monthly'});
    }

    _onMultiRateChange = rateId => {
        const {
            isMonthly,
            searchParams,
            setNewSpotRate
        } = this.props;
        const {
            updatedSpotData
        } = this.state;
        const rateType = (isMonthly) ? 'monthly_rates' : 'hourly_rates';
        const newRate = find(updatedSpotData[rateType], {fullRule: parseInt(rateId, 10)});
        const newSpotData = {
            ...updatedSpotData,
            selectedRate: false
        };

        if (newRate) {
            newRate.fullUrl = SpotUtils.buildCheckoutURL(newSpotData, newRate, searchParams);
            newSpotData.selectedRate = newRate;
        }

        this.setState({
            updatedSpotData: newSpotData
        });

        setNewSpotRate(newSpotData);
    }

    _onRemoveIndex = () => {
        this.setState({
            activeContentIndex: 0,
            rateDetailsSection: null
        });
    }

    _onWhyTimeExtendedClick = () => {
        this.setState({
            activeContentIndex: 1
        });
    }

    _onShowRateDetailsClick = section => {
        this.setState({
            activeContentIndex: 2,
            rateDetailsSection: (isString(section)) ? section : null
        });
    }

    _onSeeLargerImagesClick = () => {
        this.setState({
            activeContentIndex: 3
        });
    }

    _onOnlineCommuterRateClick = () => {
        this.setState({
            activeContentIndex: 4
        });
    }

    _onVehicleRestrictionsClick = () => {
        this.setState({
            activeContentIndex: 5
        });
    }

    _initSpotDetails() {
        this._loadRates();

        SegmentUtils.track({
            event: 'opened_spot_details'
        });
    }

    _loadRates() {
        const {
            spot,
            isMonthly,
            starts,
            ends,
            searchParams,
            event,
            timezone
        } = this.props;

        this._rateLoader = SpotUtils.loadRates({
            spotId: spot.spotId,
            starts,
            ends,
            isMonthly,
            timezone,
            searchDataParams: searchParams,
            spot,
            eventId: (event) ? event.id : null,
            onSuccessWithRates: updatedSpotData => {
                this.setState({
                    updatedSpotData
                });

                this._rateLoader = null;
            },
            onSuccessWithoutRates: () => {
                GTMUtils.push({
                    event: 'GAEvent',
                    eventCategory: 'automatic',
                    eventAction: 'rate_unavailable',
                    eventLabel: (isMonthly) ? 'monthly_rate_toggle' : 'transient_rate_toggle',
                    eventValue: undefined, // eslint-disable-line no-undefined
                    hitCallback: undefined // eslint-disable-line no-undefined
                });

                this._handleRateLoadError('Sorry, no rate could be found.');
            },
            onError: error => {
                this._handleRateLoadError('There was an error, please try again.');
            }
        });
    }

    _handleRateLoadError(message) {
        this.props.sendNotification({
            id: uuidV4(),
            type: 'error',
            message
        });

        this._rateLoader = null;
    }

    render() {
        const {
            spot,
            user,
            isMonthly,
            starts,
            ends,
            onHidden,
            cancelAllSpotRequests,
            pushTo,
            isRebookingReservation
        } = this.props;
        const {
            activeContentIndex,
            updatedSpotData,
            rateDetailsSection
        } = this.state;
        const spotData = (updatedSpotData) ? updatedSpotData : spot;
        const {
            selectedRate,
            facility,
            distance,
            distanceInMiles,
            drivingDuration,
            walkingDuration,
            operator_notes: operatorNotes,
            spot_notes: spotNotes,
            oversizedFeeMessage,
            vehicleRestriction
        } = spotData;
        const {
            getting_here: gettingHere,
            redemption_instructions: redemptionInstructions,
            post_purchase_instructions: postPurchaseInstructions
        } = facility;
        const isAdmin = user.isAdmin;
        const hasGettingHere = (gettingHere && gettingHere.length > 0);
        const hasRedemption = ((redemptionInstructions && redemptionInstructions.length > 0) || (postPurchaseInstructions && postPurchaseInstructions.length > 0));
        const hasAccessHours = selectedRate && !isUndefined(selectedRate.access_hours_formatted || selectedRate.display_hours_comment);
        const adminTabClasses = classNames(
            {'Icon-red': (spotNotes && spotNotes.length) || (operatorNotes && operatorNotes.length)}
        );

        if (!selectedRate) {
            return null;
        }

        return (
            <Modal
                className="SpotDetailsModal"
                renderInBody={false}
                trackingPageView="SpotDetail"
                activeContentIndex={activeContentIndex}
                onRemoveIndex={this._onRemoveIndex}
                onHidden={onHidden}
            >
                {(activeContentIndex === 0) &&
                    <ModalContent className="SpotDetailsModal-main">
                        <ReservationDetailsDesktop
                            isMonthly={isMonthly}
                            isAdmin={isAdmin}
                            spot={spotData}
                            starts={starts}
                            ends={ends}
                            onMultiRateChange={this._onMultiRateChange}
                            onTransientAvailableClick={this._onTransientAvailableClick}
                            onMonthlyAvailableClick={this._onMonthlyAvailableClick}
                            onWhyTimeExtendedClick={this._onWhyTimeExtendedClick}
                            onRateDetailsClick={this._onShowRateDetailsClick}
                            onOnlineCommuterRateClick={this._onOnlineCommuterRateClick}
                            onVehicleRestrictionsClick={this._onVehicleRestrictionsClick}
                            cancelAllSpotRequests={cancelAllSpotRequests}
                            pushTo={pushTo}
                            isRebookingReservation={isRebookingReservation}
                        />
                        <Tabs ref={node => { this._tabs = node; }}>
                            <TabNavigation>
                                <Tab label="Spot Details">Spot Details</Tab>
                                {(hasGettingHere) ? <Tab label="Getting There">Getting There</Tab> : false}
                                {(hasRedemption) ? <Tab label="How To Redeem">How To Redeem</Tab> : false}
                                {(hasAccessHours) ? <Tab label="Access Hours">Access Hours</Tab> : false}
                                {(isAdmin)
                                    ? (
                                        <Tab label="Admin">
                                            <Icon
                                                className={adminTabClasses}
                                                name="settings"
                                            />
                                        </Tab>
                                    ) : false
                                }
                            </TabNavigation>
                            <TabContent>
                                <TabPanel>
                                    <SpotInfoTabDesktop
                                        user={user}
                                        selectedRate={selectedRate}
                                        isMonthly={isMonthly}
                                        facility={facility}
                                        distance={distance}
                                        distanceInMiles={distanceInMiles}
                                        drivingDuration={drivingDuration}
                                        walkingDuration={walkingDuration}
                                        onSeeLargerImagesClick={this._onSeeLargerImagesClick}
                                        onShowRateDetailsClick={this._onShowRateDetailsClick}
                                    />
                                </TabPanel>
                                {(hasGettingHere)
                                    ? (
                                        <TabPanel>
                                            <GettingThere
                                                facility={facility}
                                                rate={selectedRate}
                                                isMonthly={isMonthly}
                                            />
                                        </TabPanel>
                                    ) : false
                                }
                                {(hasRedemption)
                                    ? (
                                        <TabPanel>
                                            <HowToRedeem
                                                facility={facility}
                                                rate={selectedRate}
                                                isMonthly={isMonthly}
                                            />
                                        </TabPanel>
                                    ) : false
                                }
                                {(hasAccessHours)
                                    ? (
                                        <TabPanel>
                                            <AccessHours
                                                facility={facility}
                                                rate={selectedRate}
                                            />
                                        </TabPanel>
                                    ) : false
                                }
                                {(isAdmin)
                                    ? (
                                        <TabPanel>
                                            <HeroControlsTabDesktop
                                                spotId={facility.parking_spot_id}
                                                operatorNotes={operatorNotes}
                                                spotNotes={spotNotes}
                                            />
                                        </TabPanel>
                                    ) : false
                                }
                            </TabContent>
                        </Tabs>
                    </ModalContent>
                }
                {(activeContentIndex === 1) &&
                    <ModalContent>
                        <WhyTimeExtendedModalContent
                            requestedStart={starts}
                            requestedEnd={ends}
                            rate={selectedRate}
                        />
                    </ModalContent>
                }
                {(activeContentIndex === 2) &&
                    <ModalContent className="SpotDetailsModal-rate-details">
                        <RateDetailsModalContent
                            rate={selectedRate}
                            facility={facility}
                            section={rateDetailsSection}
                        />
                    </ModalContent>
                }
                {(activeContentIndex === 3) &&
                    <ModalContent>
                        <LargerImagesModalContentDesktop
                            images={facility.images}
                            facilityTitle={facility.title}
                        />
                    </ModalContent>
                }
                {(activeContentIndex === 4) &&
                    <ModalContent>
                        <OnlineCommuterRateModalContent
                            description={selectedRate.online_commuter_rate_description}
                        />
                    </ModalContent>
                }
                {(activeContentIndex === 5) &&
                    <ModalContent>
                        <OversizedFeeDetailsModalContent
                            oversizedFeeMessage={oversizedFeeMessage}
                            vehicleRestriction={vehicleRestriction}
                        />
                    </ModalContent>
                }
            </Modal>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {
            data: {
                timezone
            }
        },
        user: {
            data: userData
        },
        checkout: {
            isRebookingReservation
        },
        searchRequest
    } = state;
    const {
        monthly,
        starts,
        ends
    } = searchRequest;

    return {
        user: userData,
        isMonthly: monthly,
        starts,
        ends,
        timezone,
        searchParams: searchRequest,
        isRebookingReservation
    };
};

const mapDispatchToProps = {
    setNewSpotRate: setSpotSelectedRate,
    setSearchTerm: setTerm,
    cancelAllSpotRequests: cancelSpotRequests,
    pushTo: push,
    sendNotification: notify
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotDetailsModalDesktop);
